/**
 * CollapsibleMarkdown - Wrapper for AI chat responses with:
 * 1. Section-based collapsible tabs (Answer, Connections, Visual Guide, etc.)
 * 2. Minimize button for ALL messages to collapse to preview
 */

import React, { useState, useMemo, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { MarkdownRenderer } from '../MarkdownRenderer';
import { CollapsibleSection } from './CollapsibleSection';
import { parseMarkdownSections, ParsedMarkdown } from '../../utils/markdownSectionParser';

interface CollapsibleMarkdownProps {
  content: string;
  className?: string;
  // MarkdownRenderer passthrough props
  onImageClick?: (src: string) => void;
  onPageLinkClick?: (bookId: string, pageNumber: number) => void;
  onNoteClick?: (noteId: string) => void;
  onTimestampClick?: (sec: number) => void;
  // Behavior props
  isStreaming?: boolean;
  defaultExpandFirst?: boolean;
  previewLength?: number;
}

/**
 * Truncate text to a certain length for preview
 */
function truncateContent(content: string, maxLength: number): { preview: string; isTruncated: boolean } {
  const plainText = content
    .replace(/!\[.*?\]\(.*?\)/g, '[image]')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/#{1,6}\s/g, '')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .trim();

  if (plainText.length <= maxLength) {
    return { preview: content, isTruncated: false };
  }

  let truncateAt = maxLength;
  while (truncateAt > 0 && plainText[truncateAt] !== ' ') {
    truncateAt--;
  }
  if (truncateAt === 0) truncateAt = maxLength;

  const preview = plainText.substring(0, truncateAt).trim() + '...';
  return { preview, isTruncated: true };
}

export const CollapsibleMarkdown: React.FC<CollapsibleMarkdownProps> = ({
  content,
  className = '',
  onImageClick,
  onPageLinkClick,
  onNoteClick,
  onTimestampClick,
  isStreaming = false,
  defaultExpandFirst = true,
  previewLength = 150
}) => {
  // Minimize state for the entire message - start minimized by default
  const [isMinimized, setIsMinimized] = useState(true);

  // Parse sections from markdown content
  const parsedMarkdown: ParsedMarkdown = useMemo(() => {
    return parseMarkdownSections(content);
  }, [content]);

  // Track section IDs for detecting content changes
  const sectionIdsKey = useMemo(() => {
    return parsedMarkdown.sections.map(s => s.id).join(',');
  }, [parsedMarkdown.sections]);

  const prevSectionIdsRef = useRef<string>('');

  // Track expanded state for each section
  const [expandedSections, setExpandedSections] = useState<Set<string>>(() => {
    if (!parsedMarkdown.hasMultipleSections) return new Set();
    const initial = new Set<string>();
    if (defaultExpandFirst && parsedMarkdown.sections.length > 0) {
      initial.add(parsedMarkdown.sections[0].id);
    }
    return initial;
  });

  // Reset expanded state when content changes
  useEffect(() => {
    if (sectionIdsKey !== prevSectionIdsRef.current && parsedMarkdown.hasMultipleSections) {
      const newExpanded = new Set<string>();
      if (defaultExpandFirst && parsedMarkdown.sections.length > 0) {
        newExpanded.add(parsedMarkdown.sections[0].id);
      }
      setExpandedSections(newExpanded);
      prevSectionIdsRef.current = sectionIdsKey;
    }
  }, [sectionIdsKey, defaultExpandFirst, parsedMarkdown.hasMultipleSections, parsedMarkdown.sections]);

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  // Get preview for minimized state
  const { preview, isTruncated } = useMemo(() => {
    return truncateContent(content, previewLength);
  }, [content, previewLength]);

  // Show minimize button if content is long enough
  const showMinimizeButton = !isStreaming && isTruncated;

  // If streaming, show full content without any collapse
  if (isStreaming) {
    return (
      <div className={className}>
        <MarkdownRenderer
          content={content}
          className="text-sm text-slate-800 dark:text-[#E2E8F0]"
          onImageClick={onImageClick}
          onPageLinkClick={onPageLinkClick}
          onNoteClick={onNoteClick} onTimestampClick={onTimestampClick}
        />
      </div>
    );
  }

  // If minimized, show preview only
  if (isMinimized) {
    return (
      <div className={className}>
        {/* Expand button at TOP */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsMinimized(false);
          }}
          className="mb-2 flex items-center gap-1 text-xs text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-300 transition-colors"
        >
          <ChevronDownIcon className="h-3 w-3" />
          <span>Read more</span>
        </button>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.15 }}
        >
          <p className="text-sm text-slate-800 dark:text-[#E2E8F0] leading-relaxed">
            {preview}
          </p>
        </motion.div>
      </div>
    );
  }

  // Full content view
  return (
    <div className={className}>
      {/* Minimize button at TOP - like Mac window controls */}
      {showMinimizeButton && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsMinimized(true);
          }}
          className="mb-2 flex items-center gap-1 text-xs text-slate-400 dark:text-slate-500 hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors"
        >
          <ChevronUpIcon className="h-3 w-3" />
          <span>Minimize</span>
        </button>
      )}

      {/* If multiple sections detected, show collapsible section tabs */}
      {parsedMarkdown.hasMultipleSections ? (
        <div className="space-y-2">
          {/* Show intro text before sections if present */}
          {parsedMarkdown.introText && (
            <div className="mb-3">
              <MarkdownRenderer
                content={parsedMarkdown.introText}
                className="text-sm text-slate-800 dark:text-[#E2E8F0]"
                onImageClick={onImageClick}
                onPageLinkClick={onPageLinkClick}
                onNoteClick={onNoteClick} onTimestampClick={onTimestampClick}
              />
            </div>
          )}

          {/* Render collapsible section tabs */}
          {parsedMarkdown.sections.map((section) => (
            <CollapsibleSection
              key={section.id}
              section={section}
              isExpanded={expandedSections.has(section.id)}
              onToggle={() => toggleSection(section.id)}
              onImageClick={onImageClick}
              onPageLinkClick={onPageLinkClick}
              onNoteClick={onNoteClick} onTimestampClick={onTimestampClick}
            />
          ))}
        </div>
      ) : (
        // No sections - render as regular markdown
        <MarkdownRenderer
          content={content}
          className="text-sm text-slate-800 dark:text-[#E2E8F0]"
          onImageClick={onImageClick}
          onPageLinkClick={onPageLinkClick}
          onNoteClick={onNoteClick} onTimestampClick={onTimestampClick}
        />
      )}
    </div>
  );
};

export default CollapsibleMarkdown;
